/**
 * Stores > Store users at /stores/users.
 *
 * Admin page for assigning users to stores. One form does the whole
 * assignment: email + store picker + role. Creating a mapping also grants the
 * Stores Viewer access level server-side (when the email holds no stores grant
 * yet), so a new store account is usable immediately: the store tools appear
 * on sign-in and its counts / check-ins land on the right store. The grid
 * lists every mapping with inline role and enable/disable controls.
 *
 * Gated by stores:admin. Columns sort via the shared SortableHeader with
 * URL-encoded ?sort=col:asc|desc, and the toolbar has an XLSX export hitting
 * /v1/stores/admin/users/export (honors the store filter), per the grid
 * standards (COCKPIT.md 2.7).
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, UserPlus } from 'lucide-react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useStoreUsers,
  useCreateStoreUser,
  useUpdateStoreUser,
  useScorecard,
  storeUsersExportUrl,
  isNotAvailable,
  denverToday,
  type StoreUserRow,
  storeLabel,
} from '@/lib/useStores';
import { StoresHeader, AccessDeniedCard, NotAvailableCard, ErrorCard } from './storesShared';

// Client-side sort: the list endpoint returns every mapping in one response
// (~50 rows), so sorting stays in the browser like the variance grid.
const SORT_COLUMNS = ['email', 'store', 'role', 'status'] as const;
type SortColumn = (typeof SORT_COLUMNS)[number];

function parseSortParam(raw: string | null): { column: SortColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!SORT_COLUMNS.includes(col as SortColumn)) return null;
  return { column: col as SortColumn, dir: dir === 'desc' ? 'desc' : 'asc' };
}

function sortUsers(
  rows: StoreUserRow[],
  column: SortColumn,
  dir: SortDir,
  storeNameByNo: Map<string, string>,
): StoreUserRow[] {
  const key = (u: StoreUserRow): string =>
    column === 'store'
      ? `${storeNameByNo.get(u.store_no) ?? ''} ${u.store_no}`.trim().toLowerCase()
      : String(u[column] ?? '').toLowerCase();
  const sorted = [...rows].sort((a, b) => key(a).localeCompare(key(b)));
  return dir === 'desc' ? sorted.reverse() : sorted;
}

const inputClass =
  'rounded-md border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink ' +
  'focus:border-amp-olive focus:outline-none dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';

export function StoreUsers() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.users', user?.superAdmin);

  const [filterStore, setFilterStore] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = parseSortParam(searchParams.get('sort'));
  const sortBy = sort?.column ?? null;
  const sortDir: SortDir = sort?.dir ?? 'asc';
  const toggleSort = (col: SortColumn) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (sortBy !== col) next.set('sort', `${col}:asc`);
        else if (sortDir === 'asc') next.set('sort', `${col}:desc`);
        else next.delete('sort');
        return next;
      },
      { replace: true },
    );
  };
  const usersQ = useStoreUsers(filterStore || undefined);
  const scoreQ = useScorecard(denverToday()); // active-store list for the pickers
  const createM = useCreateStoreUser();

  const [email, setEmail] = useState('');
  const [storeNo, setStoreNo] = useState('');
  const [role, setRole] = useState<'manager' | 'staff'>('manager');
  const [notice, setNotice] = useState<string | null>(null);

  if (user && !canView) return <AccessDeniedCard what="Store user administration" />;
  if (isNotAvailable(usersQ.error)) {
    return (
      <div>
        <StoresHeader title="Store users" />
        <NotAvailableCard what="Store user administration" />
      </div>
    );
  }

  const stores = [...(scoreQ.data?.stores ?? [])].sort((a, b) => a.store_no - b.store_no);
  const storeNameByNo = new Map(stores.map((s) => [String(s.store_no), s.store_name]));
  const rawUsers = usersQ.data?.users ?? [];
  const users = sortBy ? sortUsers(rawUsers, sortBy, sortDir, storeNameByNo) : rawUsers;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    createM.mutate(
      { email: email.trim(), store_no: storeNo, role },
      {
        onSuccess: (d) => {
          setEmail('');
          setNotice(
            d.grant_created
              ? `${d.email} assigned to store ${d.store_no} and granted Stores access.`
              : `${d.email} assigned to store ${d.store_no} (already had a Stores access grant).`,
          );
        },
      },
    );
  };

  const addForm = (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        required
        placeholder="user@lolliandpops.com"
        className={`${inputClass} min-w-[220px]`}
      />
      <select value={storeNo} onChange={(e) => setStoreNo(e.target.value)} required className={inputClass}>
        <option value="" disabled>
          Store…
        </option>
        {stores.map((s) => (
          <option key={s.store_no} value={String(s.store_no)}>
            {storeLabel(s.store_no, s.store_name)}
          </option>
        ))}
      </select>
      <select
        value={role}
        onChange={(e) => setRole(e.target.value as 'manager' | 'staff')}
        className={inputClass}
      >
        <option value="manager">Manager</option>
        <option value="staff">Staff</option>
      </select>
      <button
        type="submit"
        disabled={createM.isPending || !email.trim() || !storeNo}
        className="inline-flex items-center gap-1.5 rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-amp-olive-dark"
      >
        <UserPlus className="h-4 w-4" aria-hidden /> Assign
      </button>
      <select
        value={filterStore}
        onChange={(e) => setFilterStore(e.target.value)}
        aria-label="Filter by store"
        className={`${inputClass} ml-auto`}
      >
        <option value="">All stores</option>
        {stores.map((s) => (
          <option key={s.store_no} value={String(s.store_no)}>
            {storeLabel(s.store_no, s.store_name)}
          </option>
        ))}
      </select>
      <a
        href={storeUsersExportUrl(filterStore || undefined)}
        className="inline-flex items-center gap-1.5 rounded-md border border-amp-line px-3 py-1.5 text-sm font-medium text-amp-ink hover:border-amp-olive dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:border-amp-olive-dark"
      >
        <Download className="h-4 w-4" aria-hidden /> Export
      </a>
    </form>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <StoresHeader
        title="Store users"
        subtitle="Assign an account to a store. New assignments also get the Stores access grant automatically."
      />
      {notice && (
        <p className="mb-3 rounded-md bg-green-100 px-3 py-2 text-sm text-green-800 dark:bg-green-900/40 dark:text-green-300">
          {notice}
        </p>
      )}
      {createM.error && <div className="mb-3"><ErrorCard message={createM.error.message} /></div>}
      <DataGridShell total={users.length} loaded={users.length} countNoun="assignments" toolbar={addForm}>
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Email"
                sorted={sortBy === 'email' ? sortDir : null}
                onToggle={() => toggleSort('email')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Store"
                sorted={sortBy === 'store' ? sortDir : null}
                onToggle={() => toggleSort('store')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Role"
                sorted={sortBy === 'role' ? sortDir : null}
                onToggle={() => toggleSort('role')}
                thClassName="px-3 py-2 w-[130px]"
              />
              <SortableHeader
                label="Status"
                sorted={sortBy === 'status' ? sortDir : null}
                onToggle={() => toggleSort('status')}
                thClassName="px-3 py-2 w-[130px]"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {usersQ.isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading assignments...
                </td>
              </tr>
            ) : usersQ.error ? (
              <tr>
                <td colSpan={4} className="px-4 py-6">
                  <ErrorCard message={usersQ.error.message} />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No store assignments yet.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <UserTr key={u.id} u={u} storeName={storeNameByNo.get(u.store_no)} />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function UserTr({ u, storeName }: { u: StoreUserRow; storeName?: string }) {
  const updateM = useUpdateStoreUser();
  return (
    <tr className="bg-amp-white dark:bg-amp-dark-surface">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{u.email}</td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        {storeName ? `${storeName} (${u.store_no})` : u.store_no}
      </td>
      <td className="px-3 py-2">
        <select
          value={u.role}
          disabled={updateM.isPending}
          onChange={(e) => updateM.mutate({ id: u.id, role: e.target.value as 'manager' | 'staff' })}
          className={inputClass}
          aria-label={`Role for ${u.email}`}
        >
          <option value="manager">Manager</option>
          <option value="staff">Staff</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <select
          value={u.status}
          disabled={updateM.isPending}
          onChange={(e) =>
            updateM.mutate({ id: u.id, status: e.target.value as 'active' | 'disabled' })
          }
          className={inputClass}
          aria-label={`Status for ${u.email}`}
        >
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </td>
    </tr>
  );
}
